// IE11 does not support native Array.includes()
import includes from "lodash/includes";
import camelCase from "lodash/camelCase";

// Constants
import { ROLES } from "constants/authorization";
import { MENU_ITEMS } from "constants/menu";
import { PATHS } from "constants/routing";

// Utils
import { isPosing } from "utils/posing/posing";

// Use title & path to form key, due to changeable MENU_ITEMS' properties based on feature
const MENU_ITEM_TITLEPATH_AUTH_MAP = {
  [camelCase(
    `${MENU_ITEMS[PATHS.PEOPLE].title}${MENU_ITEMS[PATHS.PEOPLE].path}`
  )]: canAccessPeoplePage,
  [camelCase(
    `${MENU_ITEMS[PATHS.DOCUMENTS].title}${MENU_ITEMS[PATHS.DOCUMENTS].path}`
  )]: canAccessDocumentsPage,
  [camelCase(
    `${MENU_ITEMS[PATHS.CLAIMS].title}${MENU_ITEMS[PATHS.CLAIMS].path}`
  )]: canAccessClaimsPage,
  [camelCase(
    `${MENU_ITEMS[PATHS.ANALYTICS].title}${MENU_ITEMS[PATHS.ANALYTICS].path}`
  )]: canAccessAnalyticsPage
};

/**
 * Determines if the given role is an authorized role.
 * @param {string} role Role to check
 * @param {string[]} authorizedRoles List of authorized roles
 * @returns {boolean}
 */
export function hasAuthorizedRole(role, authorizedRoles) {
  return includes(authorizedRoles, role);
}

/**
 * Determines if the given role has access to the Account Page.
 * @param {string} role Role to check
 * @returns {boolean}
 */
export function hasAdminAccess(role) {
  return hasAuthorizedRole(role, [ROLES.ADMIN]);
}

/**
 * Determines if the given role has access to the Claims Page.
 * @param {string} role Role to check
 * @returns {boolean}
 */
export function canAccessClaimsPage(role) {
  return hasAuthorizedRole(role, [ROLES.ADMIN, ROLES.FULL, ROLES.REPORTING]);
}

/**
 * Determines if the given role has access to the Claims Page.
 * @param {string} role Role to check
 * @returns {boolean}
 */
export function canAccessAnalyticsPage(role) {
  return hasAuthorizedRole(role, [ROLES.ADMIN, ROLES.FULL, ROLES.REPORTING]);
}

/**
 * Determines if the given role has access to the Documents Page.
 * @param {string} role Role to check
 * @returns {boolean}
 */
export function canAccessDocumentsPage(role) {
  return hasAuthorizedRole(role, [
    ROLES.ADMIN,
    ROLES.FULL,
    ROLES.REPORTING,
    ROLES.PEOPLE,
    ROLES.LIMITED
  ]);
}

/**
 * Determines if the given role has access to the People Page.
 * @param {string} role Role to check
 * @returns {boolean}
 */
export function canAccessPeoplePage(role) {
  return hasAuthorizedRole(role, [ROLES.ADMIN, ROLES.FULL, ROLES.PEOPLE]);
}

/**
 * Determines if the given role has access to the Change Password Page.
 * @returns {boolean}
 */
export function canAccessChangePasswordPage() {
  return !isPosing();
}

/**
 * Gets the list of accessible menu items based on the given role.
 * @param {string} role Role to check
 * @param {object[]} menuItems List of menu items to filter
 * @returns {object[]} List of filtered menu items
 */
export function getAuthorizedMenuItems(role, menuItems = []) {
  return menuItems.filter(menuItem => {
    const authorizer = MENU_ITEM_TITLEPATH_AUTH_MAP[camelCase(`${menuItem.title}${menuItem.path}`)];
    return authorizer ? authorizer(role) : true;
  });
}
